import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { TopPromotion } from '@/types';
import { ChevronLeft, ChevronRight, Tag, Copy, Check, ExternalLink, Lock, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Session } from '@supabase/supabase-js';

interface OffersCarouselProps {
    session: Session | null;
    onAuthOpen: () => void;
    isExpanded: boolean;
    onToggleExpand: () => void;
}

const PLATFORM_BADGE_COLORS = {
    'mercadolivre': 'bg-yellow-400 text-yellow-900',
    'amazon': 'bg-gradient-to-r from-gray-700 to-black text-white',
    'shopee': 'bg-orange-500 text-white',
    'magalu': 'bg-blue-400 text-white',
    'aliexpress': 'bg-red-500 text-white'
}

const PLATFORM_BADGE_LABELS = {
    'mercadolivre': 'ML',
    'amazon': 'Amazon',
    'shopee': 'Shopee',
    'magalu': 'Magalu',
    'aliexpress': 'AliExpress'
}

export const OffersCarousel = ({ session, onAuthOpen, isExpanded, onToggleExpand }: OffersCarouselProps) => {
    const [promotions, setPromotions] = useState<TopPromotion[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const loadingMoreRef = useRef(false);

    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const [copiedCouponId, setCopiedCouponId] = useState<string | null>(null);

    const offersPerPage = import.meta.env.VITE_OFFERS_PER_PAGE || 20;

    const fetchedRef = useRef(false);

    useEffect(() => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;
        loadPromotions(0);
    }, []);

    const loadPromotions = async (pageIndex: number) => {
        if (pageIndex > 0 && loadingMoreRef.current) return;

        try {
            loadingMoreRef.current = true;
            if (pageIndex === 0) setLoading(true);

            const from = pageIndex * offersPerPage;
            const to = from + offersPerPage - 1;

            const { data, error } = await supabase
                .from('top_promotions')
                .select('*')
                .eq('is_active', true)
                .or(`starts_at.is.null,starts_at.lte.${new Date().toISOString()}`)
                .or(`ends_at.is.null,ends_at.gt.${new Date().toISOString()}`)
                .order('position', { ascending: true })
                .order('created_at', { ascending: false })
                .range(from, to);

            if (error) throw error;

            const newPromotions = data || [];

            setPromotions(prev => pageIndex === 0 ? newPromotions : [...prev, ...newPromotions]);
            setHasMore(newPromotions.length === offersPerPage);
            setPage(pageIndex);

        } catch (error) {
            console.error('Error fetching promotions:', error);
        } finally {
            setLoading(false);
            loadingMoreRef.current = false;
        }
    };

    const handleScroll = () => {
        if (scrollContainerRef.current && !isExpanded) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);

            // Infinite scroll trigger (when 600px from end)
            if (hasMore && !loadingMoreRef.current && !loading && (scrollWidth - (scrollLeft + clientWidth) < 600)) {
                if (!session) {
                    onAuthOpen();
                    return;
                }
                loadPromotions(page + 1);
            }
        }
    };

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 320; // Width of a card + gap
            const currentScroll = scrollContainerRef.current.scrollLeft;
            const targetScroll = direction === 'left'
                ? currentScroll - scrollAmount
                : currentScroll + scrollAmount;

            scrollContainerRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            });
        }
    };

    const copyCoupon = (code: string, id: string) => {
        navigator.clipboard.writeText(code);
        setCopiedCouponId(id);
        setTimeout(() => setCopiedCouponId(null), 2000);
    };

    if (loading) return null; // Or a skeleton loader
    if (promotions.length === 0) return null;

    return (
        <div className="w-full my-12 relative group/carousel">
            <div className="relative flex items-center justify-center mb-4 px-4 sm:px-0">
                <div className="flex items-center gap-2">
                    <Tag className="w-5 h-5 text-pink-500" />
                    <h2 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-violet-500">
                        Ofertas em Destaque
                    </h2>
                </div>
                <button
                    onClick={onToggleExpand}
                    className="absolute right-4 sm:right-0 p-2 text-gray-400 hover:text-pink-500 dark:hover:text-pink-400 transition-colors bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700"
                    title={isExpanded ? "Ver menos" : "Ver todas"}
                >
                    {isExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>
            </div>

            {/* Navigation Buttons (Only shown when NOT expanded) */}
            {!isExpanded && canScrollLeft && (
                <button
                    onClick={() => scroll('left')}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -ml-4 z-10 w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:scale-110 transition-transform hidden sm:flex"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
            )}

            {!isExpanded && canScrollRight && (
                <button
                    onClick={() => scroll('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 -mr-4 z-10 w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:scale-110 transition-transform hidden sm:flex"
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            )}

            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className={`
                    ${isExpanded
                        ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-fr px-4 sm:px-0'
                        : 'flex gap-4 overflow-x-auto pb-4 px-4 sm:px-0 scrollbar-hide snap-x snap-mandatory'}
                `}
                style={!isExpanded ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } : {}}
            >
                {promotions.map((promo) => (
                    <div
                        key={promo.id}
                        className={`
                            ${isExpanded ? 'w-full' : 'flex-shrink-0 w-[240px]'}
                            bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all overflow-hidden snap-center group flex flex-col h-full
                        `}
                    >
                        {/* Image Area */}
                        <a
                            href={(!session && promo.promo_type === 'coupon') ? undefined : promo.target_url}
                            target={(!session && promo.promo_type === 'coupon') ? undefined : "_blank"}
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                if (!session && promo.promo_type === 'coupon') {
                                    e.preventDefault();
                                    onAuthOpen();
                                }
                            }}
                            className="block relative h-32 bg-gray-100 dark:bg-gray-900 overflow-hidden cursor-pointer"
                        >
                            <img
                                src={promo.image_url}
                                alt={promo.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute top-3 right-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                                    ${PLATFORM_BADGE_COLORS[promo.provider] || 'bg-gray-400 text-white'}`
                                }>
                                    {PLATFORM_BADGE_LABELS[promo.provider] || promo.provider}
                                </span>
                            </div>

                            {/* Discount Badge */}
                            {!!promo.discount_percent && promo.discount_percent > 0 && (
                                <div className="absolute bottom-3 left-3 bg-red-500 text-white px-2 py-1 rounded-lg text-xs font-bold">
                                    {Math.round(promo.discount_percent)}% OFF
                                </div>
                            )}
                        </a>

                        {/* Content Area */}
                        <div className="p-4 flex flex-col flex-grow">
                            <h3 className="font-bold text-gray-900 dark:text-white line-clamp-2 min-h-[2.5rem] mb-1 leading-tight" title={promo.title}>
                                {promo.title}
                            </h3>
                            {promo.subtitle && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-3 h-8">
                                    {promo.subtitle}
                                </p>
                            )}

                            {/* Price / Coupon Section */}
                            <div className="mt-auto">
                                {!session && promo.promo_type === 'coupon' ? (
                                    <div className="flex flex-col gap-3 pt-2">
                                        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-600 flex flex-col items-center justify-center text-center gap-1">
                                            <div className="p-1.5 bg-gray-200 dark:bg-gray-600 rounded-full mb-1">
                                                <Lock className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                            </div>
                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                                CUPOM EXCLUSIVO PARA USUÁRIOS LOGADOS
                                            </span>
                                        </div>
                                        <Button
                                            onClick={onAuthOpen}
                                            className="w-full h-9 text-xs"
                                            variant="secondary"
                                        >
                                            Ver cupom
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex flex-col gap-2 mb-3">
                                            {promo.coupon_code && (
                                                <div className="p-1.5 bg-green-50 dark:bg-green-900/20 rounded border border-dashed border-green-300 dark:border-green-700 flex items-center justify-between gap-2">
                                                    <div className="flex items-center gap-1.5 overflow-hidden">
                                                        <Tag className="w-3 h-3 text-green-600 dark:text-green-400 shrink-0" />
                                                        <code className="text-xs font-mono font-bold text-green-700 dark:text-green-400 truncate">
                                                            {promo.coupon_code}
                                                        </code>
                                                    </div>
                                                    <button
                                                        onClick={() => copyCoupon(promo.coupon_code!, promo.id)}
                                                        className="p-1 hover:bg-white dark:hover:bg-gray-800 rounded transition-colors"
                                                        title="Copiar cupom"
                                                    >
                                                        {copiedCouponId === promo.id ? (
                                                            <Check className="w-3 h-3 text-green-500" />
                                                        ) : (
                                                            <Copy className="w-3 h-3 text-gray-500" />
                                                        )}
                                                    </button>
                                                </div>
                                            )}

                                            {promo.coupon_description && (
                                                <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                                                    {promo.coupon_description}
                                                </p>
                                            )}

                                            {promo.price != null && (
                                                <div className="flex items-end gap-2">
                                                    <span className="text-lg font-bold text-green-600 dark:text-green-400">
                                                        R$ {promo.price?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                    </span>
                                                    {!!promo.original_price && (
                                                        <span className="text-xs text-gray-400 line-through mb-1">
                                                            R$ {promo.original_price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {promo.target_url && (
                                            <a
                                                href={promo.target_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block w-full"
                                            >
                                                <Button
                                                    className="w-full h-10 text-sm"
                                                    variant='primary'
                                                >
                                                    Ver Oferta <ExternalLink className="w-4 h-4 ml-2" />
                                                </Button>
                                            </a>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {isExpanded && hasMore && (
                <div className="mt-6 flex justify-center">
                    <Button
                        onClick={() => {
                            if (!session) {
                                onAuthOpen();
                            } else {
                                loadPromotions(page + 1);
                            }
                        }}
                        isLoading={loadingMoreRef.current}
                        variant="secondary"
                        className="w-full sm:w-auto"
                    >
                        Carregar mais ofertas
                    </Button>
                </div>
            )}
        </div>
    );
};
